import { useState } from "react";
import api, { formatError } from "../lib/api";
import { fmtDateStr } from "../lib/datetime";
import { emitDataChange } from "../lib/events";
import { useAuth } from "../context/AuthContext";
import { Button } from "./ui/button";
import { Badge } from "./ui/badge";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";
import { Label } from "./ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "./ui/dialog";
import { Eye, CheckCircle2, XCircle, Banknote, Pencil, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import TravelAdvanceFormDialog from "./TravelAdvanceFormDialog";

const STATUS_COLOR = {
  Submitted: "bg-amber-100 text-amber-800",
  Approved:  "bg-emerald-100 text-emerald-800",
  Rejected:  "bg-rose-100 text-rose-700",
  Disbursed: "bg-blue-100 text-blue-800",
  Ledgered: "bg-slate-800 text-white",
  Settled:   "bg-violet-100 text-violet-800",
};

// Format 'YYYY-MM-DD' → 'DD/MM/YYYY' without going through Date (avoids TZ shift).
// Uses the shared helper from `lib/datetime`. Local alias for readability.

/**
 * Action buttons rendered alongside every Travel Advance row.
 *  • Anyone — "Open" view of details.
 *  • Master / Accounts Admin — Approve / Reject (when Submitted), Mark Disbursed (when Approved).
 */
export default function TravelAdvanceActions({ row, onChanged }) {
  const { user } = useAuth();
  const isAdmin = user?.role === "master_admin" || user?.role === "accounts_admin";
  const [openView, setOpenView] = useState(false);
  const [openRemark, setOpenRemark] = useState(null); // 'approve' | 'reject' | 'disburse' | 'accounts-approve'
  const [remark, setRemark] = useState("");
  const [disburseExtra, setDisburseExtra] = useState({ disbursement_mode: "", disbursement_reference: "", disbursement_date: new Date().toISOString().slice(0, 10) });
  const [busy, setBusy] = useState(false);
  const [editOpen, setEditOpen] = useState(false);

  const status = row.status || "Submitted";
  const isAccountsApproved = !!row.accounts_approved_at;

  const submit = async (action) => {
    if (action === "reject" && !remark.trim()) { toast.error("Rejection remark is mandatory"); return; }
    setBusy(true);
    try {
      const payload = action === "disburse"
        ? { remark: remark.trim(), ...disburseExtra }
        : { remark: remark.trim() };
      const endpoint = action === "accounts-approve" ? "accounts-approve" : action;
      await api.post(`/travel-advances/${row.id}/${endpoint}`, payload);
      toast.success(
        action === "approve" ? "Advance approved" :
        action === "reject"  ? "Advance rejected — employee can resubmit" :
        action === "accounts-approve" ? "Accounts approved — awaiting disbursement" :
        "Advance marked Disbursed",
      );
      setOpenRemark(null); setRemark(""); setOpenView(false);
      // v109.9.179 — Broadcast so every related widget refreshes.
      emitDataChange("travel:updated", { kind: "advance", id: row.id, action });
      if (action === "disburse") emitDataChange("travel:disbursed", { id: row.id });
      onChanged?.();
    } catch (e) {
      toast.error(formatError(e.response?.data?.detail));
    } finally { setBusy(false); }
  };

  return (
    <div className="flex items-center justify-end gap-1.5 flex-wrap">
      <Button size="sm" variant="outline" onClick={() => setOpenView(true)} className="h-8 text-[11px]" data-testid={`adv-open-${row.id}`}>
        <Eye size={11} className="mr-1" /> Open
      </Button>
      {/* v109.9.174 — Account Team can edit approved advances (amounts /
          entries only — audit fields are backend-immutable). */}
      {isAdmin && ["Approved", "Disbursed"].includes(status) && (
        <Button
          size="sm"
          variant="outline"
          onClick={() => setEditOpen(true)}
          className="h-8 text-[11px]"
          data-testid={`adv-edit-${row.id}`}
        >
          <Pencil size={11} className="mr-1" /> Edit
        </Button>
      )}
      {/* v109.9.174 — Accounts approval before disbursement. */}
      {isAdmin && status === "Approved" && !isAccountsApproved && (
        <Button size="sm" onClick={() => { setOpenRemark("accounts-approve"); setRemark(""); }} className="h-8 text-[11px] bg-emerald-600 hover:bg-emerald-700 text-white" data-testid={`adv-accounts-approve-${row.id}`}>
          <ShieldCheck size={11} className="mr-1" /> Accounts Approve
        </Button>
      )}
      {isAdmin && status === "Approved" && isAccountsApproved && (
        <Button size="sm" onClick={() => { setOpenRemark("disburse"); setRemark(""); }} className="h-8 text-[11px] bg-blue-600 hover:bg-blue-700 text-white" data-testid={`adv-disburse-${row.id}`}>
          <Banknote size={11} className="mr-1" /> Mark Disbursed
        </Button>
      )}
      {/* v109.9.170 — Mark Ledger (locks the row for everyone) */}
      {isAdmin && status === "Disbursed" && !row.ledgered_at && (
        <Button
          size="sm"
          onClick={async () => {
            if (!window.confirm("Mark this advance as Ledgered? Once ledgered it is LOCKED for everyone and cannot be edited further.")) return;
            setBusy(true);
            try {
              await api.post(`/travel-advances/${row.id}/mark-ledger`, {});
              toast.success("Marked Ledgered — record locked");
              emitDataChange("travel:updated", { kind: "advance", id: row.id, action: "ledger" });
              emitDataChange("travel:ledgered", { id: row.id });
              onChanged && onChanged();
            } catch (e) { toast.error(formatError(e.response?.data?.detail)); }
            finally { setBusy(false); }
          }}
          disabled={busy}
          className="h-8 text-[11px] bg-slate-700 hover:bg-slate-800 text-white"
          data-testid={`adv-ledger-${row.id}`}
        >
          <Banknote size={11} className="mr-1" /> Mark Ledger
        </Button>
      )}
      {status === "Ledgered" && (
        <Badge className="text-[10px] bg-slate-800 text-white" data-keep-color>Ledgered · Locked</Badge>
      )}

      {/* View / decision dialog */}
      <Dialog open={openView} onOpenChange={setOpenView}>
        <DialogContent className="max-w-3xl max-h-[92vh] overflow-y-auto" data-testid="adv-view-dialog">
          <DialogHeader>
            <DialogTitle className="heading flex items-center gap-2">
              Travel Advance · <Badge className={STATUS_COLOR[status] || "bg-slate-100"}>{status}</Badge>
              {isAccountsApproved && status === "Approved" && <Badge className="bg-emerald-100 text-emerald-800 border-emerald-200 text-[10px]">Accounts Approved</Badge>}
            </DialogTitle>
            <div className="text-[11px] text-slate-500">
              {row.employee_name} {row.employee_code ? `· ${row.employee_code}` : ""} {row.advance_number ? ` · ${row.advance_number}` : ""}
            </div>
          </DialogHeader>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3 py-2 text-[12.5px]">
            <Row label="Employee" v={row.employee_name || row.created_by_name} />
            <Row label="Employee Code" v={row.employee_code} />
            <Row label="Reporting Manager" v={row.reporting_manager_name} />
            <Row label="Travel Purpose" v={row.travel_purpose} />
            {row.purpose_other && <Row label="Purpose (Other)" v={row.purpose_other} />}
            <Row label="Approval Flow" v={row.approval_type} />
            <Row label="From" v={(row.from_datetime || row.from_date || "").replace("T", " ").slice(0, 16)} />
            <Row label="To" v={(row.to_datetime || row.to_date || "").replace("T", " ").slice(0, 16)} />
            <Row label="Days" v={row.days_count || 0} />
            <Row label="Destination" v={row.destination} full />
            <Row
              label="CRM Client(s)"
              v={row.client_names?.length ? row.client_names.join(", ") : (row.client_name || (row.bypass_crm ? "— (Bypass)" : "—"))}
              full
            />
            <Row label="CRM Bypass" v={row.bypass_crm ? "Yes" : "No"} />
            {row.transport_modes?.length ? <Row label="Transport" v={row.transport_modes.join(", ")} /> : null}
            {row.kms ? <Row label="KMs" v={row.kms} /> : null}
            {row.fuel_cost ? <Row label="Fuel" v={`₹ ${Number(row.fuel_cost).toLocaleString("en-IN")}`} /> : null}
            {row.toll_cost ? <Row label="Toll" v={`₹ ${Number(row.toll_cost).toLocaleString("en-IN")}`} /> : null}
            {row.public_transport_cost ? <Row label="Public Transport" v={`₹ ${Number(row.public_transport_cost).toLocaleString("en-IN")}`} /> : null}
            {row.lodging_cost ? <Row label="Lodging" v={`₹ ${Number(row.lodging_cost).toLocaleString("en-IN")}`} /> : null}
            {row.food_cost ? <Row label="Food" v={`₹ ${Number(row.food_cost).toLocaleString("en-IN")}`} /> : null}
            <Row label="Total Breakdown" v={`₹ ${Number(row.total_breakdown_cost || 0).toLocaleString("en-IN")}`} />
            <Row label="Advance Amount" v={`₹ ${Number(row.advance_amount || 0).toLocaleString("en-IN")}`} />
            {row.remark && <Row label="Employee Remark" v={row.remark} full />}
            {row.rejection_remark && (
              <div className="col-span-full border border-rose-200 bg-rose-50 rounded-md p-2.5">
                <div className="text-[10px] uppercase tracking-wider font-semibold text-rose-700">Rejection Remark</div>
                <div className="text-[12px] text-slate-800 mt-0.5">{row.rejection_remark}</div>
              </div>
            )}
            {row.approval_remark && (
              <div className="col-span-full border border-emerald-200 bg-emerald-50 rounded-md p-2.5">
                <div className="text-[10px] uppercase tracking-wider font-semibold text-emerald-700">Approval Remark</div>
                <div className="text-[12px] text-slate-800 mt-0.5">{row.approval_remark}</div>
              </div>
            )}
            {row.accounts_approval_remark && (
              <div className="col-span-full border border-emerald-200 bg-emerald-50 rounded-md p-2.5">
                <div className="text-[10px] uppercase tracking-wider font-semibold text-emerald-700">Accounts Approval Remark</div>
                <div className="text-[12px] text-slate-800 mt-0.5">{row.accounts_approval_remark}</div>
              </div>
            )}
            {row.disbursed_at && (
              <Row label="Disbursed By" v={`${row.disbursed_by_name || ""} · ${new Date(row.disbursed_at).toLocaleString("en-IN")}`} full />
            )}
          </div>

          {/* Approval chain */}
          {row.approvals?.length ? (
            <div className="rounded-md border border-slate-200 dark:border-slate-700 p-2 mt-2">
              <div className="text-[10px] uppercase tracking-wider font-semibold text-slate-500 mb-1.5">Approval Chain</div>
              <div className="space-y-1.5">
                {row.approvals.map((a, i) => (
                  <div key={i} className={`flex items-center justify-between gap-2 rounded-md border px-2 py-1.5 text-[11.5px] ${
                    a.status === "approved" ? "border-emerald-200 bg-emerald-50/60" :
                    a.status === "rejected" ? "border-rose-200 bg-rose-50/60" :
                    "border-amber-200 bg-amber-50/60"
                  }`}>
                    <div className="min-w-0 flex-1">
                      <div className="font-semibold text-slate-800 truncate">{a.name || a.role_tag || "Approver"}</div>
                      {a.remark ? <div className="text-[10.5px] text-slate-600 mt-0.5 line-clamp-2">&ldquo;{a.remark}&rdquo;</div> : null}
                    </div>
                    <Badge className={`text-[10px] ${
                      a.status === "approved" ? "bg-emerald-100 text-emerald-800 border-emerald-200" :
                      a.status === "rejected" ? "bg-rose-100 text-rose-700 border-rose-200" :
                      "bg-amber-100 text-amber-800 border-amber-200"
                    }`}>{a.status}</Badge>
                  </div>
                ))}
              </div>
            </div>
          ) : null}

          <DialogFooter className="gap-2">
            {isAdmin && status === "Submitted" && (
              <>
                <Button onClick={() => { setOpenRemark("reject"); setRemark(""); }} className="bg-rose-600 hover:bg-rose-700 text-white" data-testid="adv-reject">
                  <XCircle size={13} className="mr-1.5" /> Reject
                </Button>
                <Button onClick={() => { setOpenRemark("approve"); setRemark(""); }} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="adv-approve">
                  <CheckCircle2 size={13} className="mr-1.5" /> Approve
                </Button>
              </>
            )}
            <Button variant="outline" onClick={() => setOpenView(false)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Remark / Disburse / Accounts-Approve dialog */}
      <Dialog open={!!openRemark} onOpenChange={(o) => !o && setOpenRemark(null)}>
        <DialogContent className="max-w-md" data-testid="adv-remark-dialog">
          <DialogHeader>
            <DialogTitle className="heading">
              {openRemark === "approve" ? "Approve Advance"
                : openRemark === "reject" ? "Reject Advance — reason required"
                : openRemark === "accounts-approve" ? "Accounts Team · Approve Advance"
                : "Mark Advance Disbursed"}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <div>
              <Label className="text-[11px] uppercase tracking-wider font-semibold text-slate-600">
                Remark {openRemark === "reject" ? "*" : "(optional)"}
              </Label>
              <Textarea rows={3} value={remark} onChange={(e) => setRemark(e.target.value)} placeholder={openRemark === "reject" ? "Why is this being rejected?" : ""} className="mt-1.5" data-testid="adv-remark" />
            </div>
            {openRemark === "disburse" && (
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <Label className="text-[11px] uppercase tracking-wider font-semibold text-slate-600">Mode</Label>
                  <Input value={disburseExtra.disbursement_mode} onChange={(e) => setDisburseExtra({ ...disburseExtra, disbursement_mode: e.target.value })} placeholder="UPI / NEFT / Cash" className="mt-1.5" data-testid="adv-mode" />
                </div>
                <div>
                  <Label className="text-[11px] uppercase tracking-wider font-semibold text-slate-600">Reference</Label>
                  <Input value={disburseExtra.disbursement_reference} onChange={(e) => setDisburseExtra({ ...disburseExtra, disbursement_reference: e.target.value })} placeholder="UTR / Txn id" className="mt-1.5 mono" data-testid="adv-ref" />
                </div>
                <div className="col-span-2">
                  <Label className="text-[11px] uppercase tracking-wider font-semibold text-slate-600">Date</Label>
                  <Input type="date" value={disburseExtra.disbursement_date} onChange={(e) => setDisburseExtra({ ...disburseExtra, disbursement_date: e.target.value })} className="mt-1.5" data-testid="adv-date" />
                </div>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpenRemark(null)}>Cancel</Button>
            <Button
              onClick={() => submit(openRemark)}
              disabled={busy}
              className={openRemark === "reject" ? "bg-rose-600 hover:bg-rose-700 text-white"
                : openRemark === "disburse" ? "bg-blue-600 hover:bg-blue-700 text-white"
                : "bg-emerald-600 hover:bg-emerald-700 text-white"}
              data-testid="adv-remark-submit"
            >
              {busy ? "Saving…" : (openRemark === "approve" ? "Approve"
                : openRemark === "reject" ? "Reject"
                : openRemark === "accounts-approve" ? "Accounts Approve"
                : "Mark Disbursed")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* v109.9.174 — Edit dialog with locked audit fields (banner shown by
          TravelAdvanceFormDialog when editRecord is passed). */}
      <TravelAdvanceFormDialog
        open={editOpen}
        onClose={() => setEditOpen(false)}
        onCreated={() => { setEditOpen(false); onChanged?.(); }}
        editRecord={editOpen ? row : null}
      />
    </div>
  );
}

function Row({ label, v, full }) {
  return (
    <div className={full ? "col-span-2" : ""}>
      <div className="text-[10px] uppercase tracking-wider font-semibold text-slate-500">{label}</div>
      <div className="text-slate-800 mt-0.5 whitespace-pre-wrap break-words">{v || "—"}</div>
    </div>
  );
}
